import { Component, Injector, NO_ERRORS_SCHEMA /* CUSTOM_ELEMENTS_SCHEMA */ } from '@angular/core';
import type { OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { IonicModule } from '@ionic/angular';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

import { PickupQueueService, TicketsService, UsersService } from '@shared/services';
import { ViewBasePage } from '@shared/pages';
import type { TCustomer, TTicketsViewModel, TUser } from '@shared/models';

@Component({
    'selector': 'app-ticket3Save',
    'templateUrl': './ticket3Save.page.html',
    'styleUrls': ['./ticket3Save.page.scss'],
    'standalone': true,
    'imports': [ IonicModule, CommonModule, FormsModule],
    // 'schemas': [CUSTOM_ELEMENTS_SCHEMA]
    // 'schemas': [ NO_ERRORS_SCHEMA ]
})
export class Ticket3SavePage extends ViewBasePage implements OnInit, OnDestroy {
    public vm: TTicketsViewModel;
    public serviceAdvisors: TUser[];

    constructor(private ticketsService: TicketsService, private pickupQueueService: PickupQueueService,
        private usersService: UsersService,  injector: Injector) {
        super(injector);
    }

    public ionViewWillEnter(): void {
        super.setCurrentView(this.Constants.VIEW_TICKETS_PAGE3);
    }

    public override async ngOnInit(): Promise<void> {
        super.ngOnInit();

        this.vm = super.getViewModel();
        this.vm.ticket.cellPhone = this.utilsService.formatPhoneNumberForDisplay(this.vm.ticket.cellPhone);
        this.vm.ticket.serviceAdvisor = this.sessionService.getUsersFullName;

        // Use the dealership's country code for the ticket's country code.
        const dealershipCountryCode = this.sessionService.getDealership().address.isoCountryCode;
        if (this.vm.ticket.fromDealership) {
            this.vm.ticket.dropoff.address.isoCountryCode = dealershipCountryCode;
        } else {
            this.vm.ticket.pickup.address.isoCountryCode = dealershipCountryCode;
        }

        this.pickupQueueService.initialize();
        this.serviceAdvisors = await this.usersService.getUsersByRole('serviceAdvisor');
        // this.initMoreMenu();
    }

    public override ngOnDestroy(): void {
        super.ngOnDestroy();
    }

    // public initMoreMenu(): void {
    //     this.resetPopoverMenu();
    //     this.pushPopoverMenuItem('Show Tour', 'show-ticket-3-tour');
    // }

    public showSetUserButton(): boolean {
        const show = (this.sessionService.getAccountProfile().showSetUserButton ?? false);
        return (show);
    }

    public onToggleServiceAdvisor(): void {
        let idx = this.serviceAdvisors.findIndex(user => {
            const fullName = user.firstName + ' ' + user.lastName;
            return (fullName === this.vm.ticket.serviceAdvisor);
        });
        idx = (idx === this.serviceAdvisors.length - 1) ? 0 : ++idx;
        const user = this.serviceAdvisors[idx];
        this.vm.ticket.serviceAdvisor = user.firstName + ' ' + user.lastName;
    }

    public async onSaveClick() {
        await this.uxHelperService.showSpinner('Saving...');

        try {
            this.deleteUnneededFields();
            this.vm.ticket.cellPhone = this.utilsService.unformatPhoneNumber(this.vm.ticket.cellPhone);
            // this.vm.ticket.serviceAdvisor = this.sessionService.getUsersFullName;
            this.vm.ticket.numPassengers *= 1;      // Ionic returns a string even though the type is a number.  Convert to a number.

            if (!this.vm.ticket.uid) {
                await this.ticketsService.add(this.vm.ticket);

                // When the EVENT_TICKET_CREATED event is raised, sms service will be triggered.
                // An sms message will be sent to the phone number.  When the firebase functions
                // successfully send the sms, the customer will be created at that time.  This way,
                // all the customers in our database are guaranteed to have a valid cell phone number.
                const customer: TCustomer = {
                    'customerName': this.vm.ticket.passengerName,
                    'cellPhone': this.vm.ticket.cellPhone,
                    'uid': '',
                    'shuttleDates': []
                };
                const data = { 'customers': [customer] };
                // this.eventsService.publish(this.Constants.EVENT_TICKET_CREATED, data.toString());
                this.eventsService.publish(this.Constants.EVENT_TICKET_CREATED, data);

                //  DO NOT DELETE THIS COMMENT.
                //  Right now the PickupQueueService is listening for new tickets.  That means all the service advisors
                //  computers will have their PickupRequestsService listening for new tickets.  Assume frontier toyota
                //  has 10 service advisors.  That means all 10 computers will be listening for newly created tickets
                //  and sending out 10 pickup requests to the same driver.  Not good.  The alternative is to have
                //  the service advisor's computer explicitly call the pickup request code.  This way only one computer
                //  will be busy doing the pickup request routines.
                if (this.vm.ticket.fromDealership === false) {
                    // Request pickup from elsewhere to dealership
                    // this.pickupQueueService.addTicketToPickupQueue(this.vm.ticket, this.availableShuttlesCallback);
                    this.pickupQueueService.addTicketToPickupQueue(this.vm.ticket);

                    // If the service advisor is on the phone with the customer and the customer has asked for a
                    // pickup, then tell the service advisor to tell the customer that the driver will be contacting
                    // them shortly.
                    const msg = 'Tell your customer, that their pickup request has been created.';
                    this.uxHelperService.showAlert('Information', msg);
                }
            } else {
                this.vm.ticket.createdDate = this.dateService.now();
                await this.ticketsService.update(this.vm.ticket);
            }
            // await this.saveCustomer(this.vm.ticket.passengerName, this.vm.ticket.cellPhone);
        } catch (err) {
            const errorMessage = this.utilsService.extractError(err);
            this.uxHelperService.showAlert('Error', errorMessage);
        } finally {
            await this.uxHelperService.hideSpinner();
            // await this.uxHelperService.showToast('Saved.');  it appears there is something very wrong with this!!!
            this.router.navigateByUrl('/tickets');
        }
    }

    private deleteUnneededFields() {
        // delete this.vm.ticket.addressPrompt;
        // delete this.vm.ticket.coordinate;
        // delete this.vm.ticket.addressVerifiedMsg;
        // delete this.vm.ticket.step1Complete;
        // delete this.vm.ticket.step2Complete;
        // delete this.vm.ticket.step3Complete;

        if (this.vm.ticket.fromDealership) {
            // Only keep dropoff variables and delete pickup variables.
            delete this.vm.ticket.pickup;
            delete this.vm.pickupFullAddressFoundMsg;
        } else {
            // Only keep pickup variables and delete dropoff variables.
            delete this.vm.ticket.dropoff;
            delete this.vm.dropoffFullAddressFoundMsg;
        }
    }
}
